/* ────────────────────────────────────────────────────────────────────────────
   HERO — primera impresión en <5s
   Layout: 2 columnas en desktop, apilado en mobile.
   ──────────────────────────────────────────────────────────────────────────── */

.section {
  position: relative;
  padding-block: calc(var(--navbar-height) + var(--space-10)) var(--space-16);
  overflow: hidden;
  isolation: isolate;
  background: var(--sand-100);
}

@media (min-width: 1024px) {
  .section {
    padding-top: var(--navbar-height);
    padding-bottom: 0;
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

/* Glow de fondo: gradiente sutil detrás del mockup */
.bgGlow {
  position: absolute;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  background:
    radial-gradient(60% 50% at 78% 32%, rgba(46, 108, 200, 0.10), transparent 70%),
    radial-gradient(45% 40% at 12% 70%, rgba(44, 168, 158, 0.06), transparent 70%);
}

.inner {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-12);
  align-items: center;
  width: 100%;
}

@media (min-width: 1024px) {
  .inner {
    grid-template-columns: minmax(0, 55fr) minmax(0, 45fr);
    gap: var(--space-16);
  }
}

/* ──── Copy column ─────────────────────────────────────────────────────────── */

.copy {
  max-width: 620px;
  align-self: center;
}

.title,
.subtitle,
.ctas {
  opacity: 0;
  transform: translateY(12px);
}

.section.mounted .title,
.section.mounted .subtitle,
.section.mounted .ctas {
  opacity: 1;
  transform: translateY(0);
  transition:
    opacity var(--duration-base) var(--ease-decelerate),
    transform var(--duration-base) var(--ease-decelerate);
  transition-delay: calc(var(--i, 0) * 80ms);
}

.title {
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: clamp(34px, 5.5vw, 60px);
  line-height: 1.04;
  letter-spacing: -0.022em;
  color: var(--color-text);
  margin-bottom: var(--space-5);
  text-wrap: balance;
}

.subtitle {
  font-family: var(--font-body);
  font-size: clamp(16px, 1.6vw, 19px);
  line-height: 1.55;
  color: var(--color-text-muted);
  margin-bottom: var(--space-8);
  max-width: 560px;
}

.ctas {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-4);
  margin-bottom: var(--space-8);
}

@media (min-width: 520px) {
  .ctas {
    flex-direction: row;
    align-items: center;
    gap: var(--space-6);
  }
}

.secondary {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  font-family: var(--font-body);
  font-size: var(--text-base);
  font-weight: 600;
  color: var(--color-primary);
  text-decoration: none;
  padding-block: var(--space-2);
  transition: color var(--duration-fast) var(--ease-standard);
}

.secondary:hover {
  color: var(--color-primary-hover);
}

.secondary:hover .secondaryArrow {
  transform: translateX(3px);
}

.secondaryArrow {
  transition: transform var(--duration-fast) var(--ease-standard);
  display: inline-flex;
}

/* Tone variants: icon color matches the value */
.tone-blue   { --tone-color: var(--blue-500);   }
.tone-jade   { --tone-color: var(--jade-500);   }
.tone-amber  { --tone-color: var(--amber-500);  }
.tone-violet { --tone-color: var(--violet-500); }

/* ──── Visual column (phone mockup) ───────────────────────────────────────── */

.visual {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-6);
  justify-self: center;
}

.phone {
  width: 200px;
  aspect-ratio: 9 / 19.5;
  filter: drop-shadow(0 22px 44px rgba(39, 35, 28, 0.16));
}

@media (min-width: 768px) {
  .phone {
    width: 220px;
  }
}

@media (min-width: 1280px) {
  .phone {
    width: 240px;
  }
}

.phoneShell {
  position: relative;
  width: 100%;
  height: 100%;
  background: linear-gradient(160deg, #2a2620 0%, #0f0d0a 100%);
  border-radius: 34px;
  padding: 7px;
  box-shadow:
    inset 0 0 0 1.5px rgba(255, 255, 255, 0.06),
    inset 0 0 0 3px rgba(0, 0, 0, 0.4);
}

.screen {
  position: relative;
  width: 100%;
  height: 100%;
  background: var(--color-bg);
  border-radius: 27px;
  overflow: hidden;
}

/* ──── Splash (brand intro) ───────────────────────────────────────────────── */

.splash {
  position: absolute;
  inset: 0;
  z-index: 10;
  background: var(--blue-900);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 1;
  transition: opacity 700ms var(--ease-standard);
}

.splashGone {
  opacity: 0;
  pointer-events: none;
}

.splashWordmark {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  font-family: var(--font-heading);
  font-weight: 800;
  font-size: 28px;
  letter-spacing: -0.02em;
  color: var(--color-text-inverse);
  opacity: 0;
  transform: translateY(6px) scale(0.96);
  animation: splashBrandIn 650ms var(--ease-decelerate) 120ms forwards;
}

.splashDot {
  width: 7px;
  height: 7px;
  border-radius: var(--radius-full);
  background: var(--color-accent);
  transform: translateY(-2px);
}

@keyframes splashBrandIn {
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* ──── Phone values list (revealed after splash) ──────────────────────────── */
/* Editorial / manifesto style: icon on top, uppercase heading-font label below,
   generous breathing room between items. */

.phoneValues {
  position: absolute;
  inset: 0;
  padding: 36px 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 30px;
}

.phoneValueItem {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;
  opacity: 0;
  transform: translateY(12px);
}

.phoneValuesReveal .phoneValueItem {
  opacity: 1;
  transform: translateY(0);
  transition:
    opacity 560ms var(--ease-decelerate),
    transform 560ms var(--ease-decelerate);
  transition-delay: calc(var(--vi, 0) * 700ms);
}

.phoneValueIcon {
  color: var(--tone-color, var(--color-primary));
  flex-shrink: 0;
}

.phoneValueLabel {
  font-family: var(--font-heading);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--color-text);
  line-height: 1;
}

/* ──── Accessibility ──────────────────────────────────────────────────────── */

.srOnly {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* ──── Reduced motion ─────────────────────────────────────────────────────── */

@media (prefers-reduced-motion: reduce) {
  .title,
  .subtitle,
  .ctas {
    opacity: 1;
    transform: none;
  }

  .splash {
    transition: none;
  }

  .splashWordmark {
    opacity: 1;
    transform: none;
    animation: none;
  }

  .phoneValueItem,
  .phoneValuesReveal .phoneValueItem {
    opacity: 1;
    transform: none;
    transition: none;
  }
}
